import React, { useState } from 'react';
import { Flex, Box, TextField } from '@radix-ui/themes';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import Logo from './logo';
import ComponentBar from './ComponentBar';
import NetworkTest from './NetworkTest';
import regions_cities from '../../json/regions_and_cities.json';
import countryMapping from '../../json/countries.json';
import Fuse from 'fuse.js';

/**
 * Maps cities and regions to an array of objects containing their names and respective country codes.
 * Also generates lists of cities, regions, and countries for search purposes.
 * 
 * - `cities`: List of cities with their country codes.
 * - `regions`: List of regions with their country codes.
 * - `countries`: List of countries.
 * - `cities_names`: Array of city names for search matching.
 * - `region_names`: Array of region names for search matching.
 * - `country_names`: Array of country names for search matching.
 * - `regions_cities_names`: Combined list of cities, regions, and countries for search functionality.
 */
const cities = regions_cities.map((country) => { return country.cities.map((value) => { return { name: value, country: country.country_code } }) }).flat()
const regions = regions_cities.map((country) => { return country.regions?.map((value) => { return { name: value, country: country.country_code } }) }).flat()
const countries = Object.values(countryMapping).map((country) => { return { name: country, country: '' } })
const cities_names = cities.map((val) => val?.name)
const region_names = regions.map((val) => val?.name)
const country_names = countries.map((val) => val?.name)
const regions_cities_names = cities.concat(regions).concat(countries)


/**
 * Navbar component that renders the navigation bar with a search feature and toggles for various map views.
 * 
 * @param {boolean} isPhone - Whether the device is a phone.
 * @param {boolean} showChartCol - Whether the chart column is visible.
 * @param {function} toggleChartCol - Function to toggle the chart column visibility.
 * @param {boolean} showFilterCol - Whether the filter column is visible.
 * @param {function} toggleFilterCol - Function to toggle the filter column visibility.
 * @param {boolean} showHelp - Whether the help menu is visible.
 * @param {function} toggleShowHelp - Function to toggle the help menu visibility.
 * @param {boolean} showMapSettings - Whether the map settings are visible.
 * @param {function} toggleShowMapSettings - Function to toggle the map settings visibility.
 * @param {function} countryClickCallback - Callback function for selecting a country.
 * @param {function} provinceClickCallback - Callback function for selecting a province.
 * @param {function} cityClickCallback - Callback function for selecting a city.
 */
function Navbar({ isPhone, showChartCol, toggleChartCol, showFilterCol, toggleFilterCol, showHelp, toggleShowHelp, showMapSettings, toggleShowMapSettings, countryClickCallback, provinceClickCallback, cityClickCallback }) {
  const [searchQuery, setSearchQuery] = useState();
  const [searchResults, setSearchResults] = useState([]);

  /**
 * Handles the search query input change and performs a fuzzy search on regions, cities, and countries.
 * 
 * @param {Event} e - The input change event.
 */
  function handleSearchChange(e) {
    const search = e.target.value;
    setSearchQuery(search)
    const options = {
      keys: ["name"],
      threshold: 0.6,  // Controls the fuzziness of the search, lower is stricter
    };
    const fuse = new Fuse(regions_cities_names, options);
    const result = fuse.search(search);
    setSearchResults(result.slice(0, 5).map((feature) => `${feature.item.name} ${feature.item.country}`))
  }


  /**
   * Handles the selection of a location from the search results and triggers the appropriate callback.
   * 
   * @param {string} region - The selected region name.
   */
  function handleLocationSelect(region) {
    const regionName = region.split(" ").slice(0, -1).join(" ");
    const countryCode = region.split(" ")[region.split(" ").length - 1];
    console.log(regionName);
    console.log(countryCode);
    if (country_names.includes(regionName)) {
      countryClickCallback(regionName);
    }
    else if (region_names.includes(regionName)) {
      provinceClickCallback(countryMapping[countryCode], regionName)
    }
    else if (cities_names.includes(regionName)) {
      cityClickCallback(countryMapping[countryCode], regionName)
    }

    else {
      alert("Invalid Region")
    }

    setSearchQuery("");
    setSearchResults([]);
  }

  return !isPhone ? (
    <Flex
      as="header"
      direction="row"
      align="center"
      justify="between"
      style={{ width: '100%', backgroundColor: '#18191b', padding: '1rem', height: '4rem', zIndex: 1, position: "fixed" }}
    >
      {/* Left item */}
      <Box style={{ color: 'white', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Logo />
      </Box>

      {/* Center item */}
      <Flex
        style={{

          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          textAlign: 'center',
          marginLeft: 'auto',
          zIndex: 20
        }}>

        <TextField.Root size="3" radius="large" placeholder="Search for a place..." onChange={handleSearchChange} value={searchQuery} >
          <TextField.Slot>
            <MagnifyingGlassIcon height="16" width="16" />
          </TextField.Slot>
          <Flex gap="4" direction="column" style={{ marginTop: '10px', position: "fixed", top: '43px', width: '230px' }}>
            {searchResults.length > 0 && (
              <ul style={{ listStyleType: 'none', padding: 0, margin: 0, border: '2px solid #444', borderRadius: '5px', maxHeight: '150px', overflowY: 'auto' }}>
                {searchResults.map((city, index) => (
                  <li
                    key={index}
                    style={{ display: 'flex', padding: '5px', cursor: 'pointer', backgroundColor: index % 2 === 0 ? '#191919' : '#000' }}
                    onClick={() => handleLocationSelect(city)}>
                    <span style={{ width: '80%', fontWeight: 'bold' }}> {city.split(" ").slice(0, -1).join(" ")} </span> <span style={{ color: 'GrayText', fontWeight: 'lighter' }}> {city.split(" ")[city.split(" ").length - 1]} </span>
                  </li>
                ))}
              </ul>
            )}
          </Flex>
        </TextField.Root>
      </Flex>

      {/* Right item */}
      <Flex style={{ height: '50px', color: 'white', display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '1rem' }}>
        <NetworkTest />
        <span style={{ width: '20px' }}></span>
        <ComponentBar showMapSettings={showMapSettings} toggleShowMapSettings={toggleShowMapSettings} showChartCol={showChartCol} toggleChartCol={toggleChartCol} showFilterCol={showFilterCol} toggleFilterCol={toggleFilterCol} showHelp={showHelp} toggleShowHelp={toggleShowHelp}></ComponentBar>
      </Flex>
    </Flex>
  ) : (
    <Flex
      as="header"
      direction="column"
      align="center"
      justify="between"
      style={{ width: '100%', backgroundColor: '#18191b', padding: '1rem', height: '8rem', zIndex: 1, position: "fixed" }}
    >
      <Flex>
        {/* Left item */}
        <Box style={{ color: 'white', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Logo />
        </Box>

        {/* Center item */}
        <Flex
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            textAlign: 'center',
            marginLeft: 'auto',
            zIndex: 20,
            marginLeft: '10px'
          }}>

          <TextField.Root size="2" radius="large" placeholder="Search for a place..." onChange={handleSearchChange} value={searchQuery} >
            <TextField.Slot>
              <MagnifyingGlassIcon height="16" width="16" />
            </TextField.Slot>
            <Flex gap="4" direction="column" style={{ marginTop: '10px', position: "fixed", top: '43px', width: '200px' }}>
              {searchResults.length > 0 && (
                <ul style={{ listStyleType: 'none', padding: 0, margin: 0, border: '2px solid #444', borderRadius: '5px', maxHeight: '150px', overflowY: 'auto' }}>
                  {searchResults.map((city, index) => (
                    <li
                      key={index}
                      style={{ display: 'flex', padding: '5px', cursor: 'pointer', backgroundColor: index % 2 === 0 ? '#191919' : '#000' }}
                      onClick={() => handleLocationSelect(city)}>
                      <span style={{ width: '80%', fontWeight: 'bold' }}> {city.split(" ").slice(0, -1).join(" ")} </span> <span style={{ color: 'GrayText', fontWeight: 'lighter' }}> {city.split(" ")[city.split(" ").length - 1]} </span>
                    </li>
                  ))}
                </ul>
              )}
            </Flex>
          </TextField.Root>
        </Flex>
      </Flex>

      {/* Right item */}
      <Flex style={{ height: '50px', color: 'white', display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '1rem' }}>
        <NetworkTest isPhone={isPhone} />
      </Flex>
    </Flex>
  );
}

export default Navbar;
